import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { API, axios } from "@/api";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Share2, SkipForward, Gift, Truck, Star, Flame, MapPin, Users, Sparkles, Trophy, X } from "lucide-react";

const CARD_ANIMATION = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -30 },
  transition: { duration: 0.4, ease: "easeOut" },
};

function StatCard({ icon: Icon, value, label, color, accent }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6" data-testid={`wrapped-card-${label?.toLowerCase().replace(/\s/g, "-")}`}>
      {Icon && <Icon className="w-10 h-10 mb-4" style={{ color: color || "#F59E0B" }} />}
      <p className="text-6xl font-black text-white mb-3">{value}</p>
      <p className="text-base text-white/60 text-center">{label}</p>
      {accent && <p className="text-sm text-amber-500/80 mt-2 text-center">{accent}</p>}
    </div>
  );
}

function SummaryCard({ data, userName, periodLabel }) {
  const mvt = data?.most_visited_truck;
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-4" data-testid="wrapped-summary-card">
      <div className="w-full max-w-sm bg-[#2C2C2E] rounded-2xl p-5 border border-white/10">
        <p className="text-center text-amber-500 font-bold text-sm mb-1">BirthdayDeaLight</p>
        <p className="text-center text-white font-black text-xl mb-0.5">{userName}</p>
        <p className="text-center text-white/40 text-xs mb-5">{periodLabel} Wrapped</p>

        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-amber-500/10 rounded-xl p-3 text-center border border-amber-500/20">
            <p className="text-2xl font-black text-amber-500">{data?.total_deals_claimed || 0}</p>
            <p className="text-[10px] text-white/50">Deals Claimed</p>
          </div>
          <div className="bg-blue-500/10 rounded-xl p-3 text-center border border-blue-500/20">
            <p className="text-2xl font-black text-blue-500">{data?.total_trucks_visited || 0}</p>
            <p className="text-[10px] text-white/50">Trucks Visited</p>
          </div>
          <div className="bg-green-500/10 rounded-xl p-3 text-center border border-green-500/20">
            <p className="text-2xl font-black text-green-500">{data?.total_punches_earned || 0}</p>
            <p className="text-[10px] text-white/50">Punches Earned</p>
          </div>
          <div className="bg-purple-500/10 rounded-xl p-3 text-center border border-purple-500/20">
            <p className="text-2xl font-black text-purple-500">{data?.total_points_earned || 0}</p>
            <p className="text-[10px] text-white/50">Points</p>
          </div>
        </div>

        {data?.favorite_cuisine && (
          <p className="text-center text-white/60 text-sm mb-1">Favorite: <span className="text-amber-500 font-medium">{data.favorite_cuisine}</span></p>
        )}
        {mvt && (
          <p className="text-center text-white/40 text-xs">Most visited: {mvt.name}</p>
        )}

        <p className="text-center text-white/20 text-[9px] mt-4">Join me on BirthdayDeaLight - birthdaydealight.com</p>
      </div>
    </div>
  );
}

export default function BirthdayWrapped() {
  const navigate = useNavigate();
  const { reportId } = useParams();
  const [data, setData] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cardIndex, setCardIndex] = useState(0);
  const [sharing, setSharing] = useState(false);
  const touchRef = useRef(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        if (reportId) {
          const res = await axios.get(`${API}/user/wrapped/${reportId}`);
          setReportData(res.data.data || res.data);
          setData(res.data);
        } else {
          const res = await axios.get(`${API}/birthday-wrapped`);
          setData(res.data);
        }
      } catch {}
      setLoading(false);
    };
    fetch();
  }, [reportId]);

  const d = reportData || data?.stats || {};
  const userName = data?.user_name || "";
  const periodLabel = reportData?.period_label || `${data?.year || new Date().getFullYear()}`;
  const topTrucks = data?.top_trucks || [];
  const topCuisines = data?.top_cuisines || [];
  const mvt = reportData?.most_visited_truck || (topTrucks[0] ? { name: topTrucks[0].name, visit_count: topTrucks[0].visits } : null);

  const cards = [
    { type: "welcome" },
    { icon: Gift, value: reportData?.total_deals_claimed ?? d?.freebies_claimed ?? 0, label: `birthday deals claimed this ${reportData ? "period" : "year"}`, color: "#F59E0B" },
    ...(mvt ? [{ icon: Truck, value: mvt.name, label: `most visited truck (${mvt.visit_count || 0} visits)`, color: "#3B82F6" }] : []),
    ...(reportData?.favorite_cuisine || topCuisines[0] ? [{ icon: MapPin, value: reportData?.favorite_cuisine || topCuisines[0], label: "your favorite cuisine", color: "#10B981" }] : []),
    { icon: Star, value: reportData?.total_points_earned ?? d?.xp ?? 0, label: "points earned", color: "#8B5CF6" },
    ...(reportData?.badges_unlocked_this_period?.length ? [{ icon: Trophy, value: reportData.badges_unlocked_this_period.length, label: "badges unlocked", color: "#EF4444" }] : []),
    ...(reportData?.streak_days ? [{ icon: Flame, value: `${reportData.streak_days} days`, label: "longest streak", color: "#F97316" }] : []),
    ...(reportData?.birthday_crawls_built ? [{ icon: MapPin, value: reportData.birthday_crawls_built, label: "crawls built", color: "#14B8A6" }] : []),
    { type: "summary" },
  ];

  const total = cards.length;
  const goNext = () => setCardIndex(i => Math.min(i + 1, total - 1));
  const goPrev = () => setCardIndex(i => Math.max(i - 1, 0));
  const skipToEnd = () => setCardIndex(total - 1);

  const handleTouchStart = (e) => { touchRef.current = e.touches[0].clientX; };
  const handleTouchEnd = (e) => {
    if (!touchRef.current) return;
    const diff = touchRef.current - e.changedTouches[0].clientX;
    if (diff > 50) goNext();
    else if (diff < -50) goPrev();
    touchRef.current = null;
  };

  const handleShare = async () => {
    if (!reportId && !data?.report_id) return;
    setSharing(true);
    try {
      const rid = reportId || data?.report_id;
      const res = await axios.post(`${API}/user/wrapped/${rid}/share-image`);
      if (navigator.share && res.data.share_image_url) {
        await navigator.share({
          title: `My ${periodLabel} Birthday Wrapped`,
          text: `Check out my birthday highlights on BirthdayDeaLight!`,
          url: window.location.href,
        });
      }
    } catch {}
    setSharing(false);
  };

  if (loading) {
    return <div className="min-h-screen bg-[#1C1C1E] flex items-center justify-center"><div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const card = cards[cardIndex];

  return (
    <div
      className="min-h-screen bg-[#1C1C1E] relative overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      data-testid="birthday-wrapped-page"
    >
      {/* Top bar */}
      <div className="absolute top-0 left-0 right-0 z-10 flex items-center justify-between px-4 py-3 safe-top">
        <button onClick={() => navigate(-1)} className="text-white/50" data-testid="wrapped-close"><X className="w-5 h-5" /></button>
        <button onClick={skipToEnd} className="text-white/40 text-xs font-medium" data-testid="wrapped-skip">Skip</button>
      </div>

      {/* Dot indicators */}
      <div className="absolute top-12 left-0 right-0 z-10 flex justify-center gap-1.5 px-6">
        {cards.map((_, i) => (
          <div key={i} className={`h-1 rounded-full transition-all ${i === cardIndex ? "bg-amber-500 w-6" : "bg-white/15 w-1.5"}`} />
        ))}
      </div>

      {/* Card content */}
      <AnimatePresence mode="wait">
        <motion.div key={cardIndex} {...CARD_ANIMATION} className="h-full">
          {card.type === "welcome" && (
            <div className="flex flex-col items-center justify-center min-h-screen px-6" data-testid="wrapped-welcome-card">
              <Sparkles className="w-12 h-12 text-amber-500 mb-4" />
              <h1 className="text-3xl font-black text-white text-center mb-2">
                Your {periodLabel} Wrapped
              </h1>
              <p className="text-white/50 text-center text-sm">is here</p>
              <p className="text-white/30 text-xs mt-8">Swipe to explore</p>
            </div>
          )}

          {card.type === "summary" && (
            <SummaryCard
              data={reportData || { total_deals_claimed: d?.freebies_claimed, total_trucks_visited: topTrucks.length, total_punches_earned: d?.punches_earned, total_points_earned: d?.xp, favorite_cuisine: topCuisines[0], most_visited_truck: mvt }}
              userName={userName}
              periodLabel={periodLabel}
            />
          )}

          {!card.type && (
            <StatCard
              icon={card.icon}
              value={card.value}
              label={card.label}
              color={card.color}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Navigation arrows */}
      <div className="absolute bottom-8 left-0 right-0 flex items-center justify-between px-6 z-10">
        <button onClick={goPrev} disabled={cardIndex === 0} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-20" data-testid="wrapped-prev">
          <ArrowLeft className="w-4 h-4 text-white" />
        </button>

        {cardIndex === total - 1 ? (
          <button
            onClick={handleShare}
            disabled={sharing}
            className="flex-1 mx-4 py-3 rounded-xl bg-amber-500 text-black font-bold text-sm flex items-center justify-center gap-2"
            data-testid="wrapped-share-btn"
          >
            <Share2 className="w-4 h-4" /> Share My Wrapped
          </button>
        ) : (
          <div className="flex-1" />
        )}

        <button onClick={goNext} disabled={cardIndex === total - 1} className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center disabled:opacity-20" data-testid="wrapped-next">
          <ArrowRight className="w-4 h-4 text-white" />
        </button>
      </div>
    </div>
  );
}
